import { ChevronDown, ChevronRight } from 'lucide-react';
import { isGuaranteedJevMode, type GuaranteedMode } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { Checkbox, cx, Input } from '../../ui/index.ts';
import { LegLampMark } from './LegLampMark.tsx';
import type { LegState } from './legPhysical.ts';
import {
  countLegStates,
  firstRemovedCount,
  groupSkusByNamespace,
  liveStates,
  manualLegCount,
  overlayFallbackOnCount,
  type LegSku,
  type PlacedLeg,
  type LegStateCounts,
} from './legSkus.ts';
import { legStateLabel, takeoverText } from './legText.ts';
import { fallbackLabelKey, modeLabelKey, skuChainMode, skuFallbackMode, skuProbeMode } from './modes.ts';

/**
 * Left rail of the leg card (#2658): namespace boxes of downstream SKUs,
 * one small lamp per physical leg, grouped 首发 ┃ 兜底 ┆ 不接流量. A red
 * lamp left of the first divider is the dangerous one: the SKU is serving
 * from fewer first-traffic legs than configured, so its row is flagged and
 * sorted first.
 */

const MODE_TEXT_TONE: Readonly<Record<GuaranteedMode, string>> = {
  act: 'text-signal',
  shadow: 'text-paper-mid',
  off: 'text-paper-low',
};

function Totals({ counts, firstRemoved }: { readonly counts: LegStateCounts; readonly firstRemoved: number }): JSX.Element {
  const t = useT();
  const items: readonly { state: LegState; label: string; count: number; always: boolean }[] = [
    { state: 'tripped', label: t('guaranteed.legs.total.red'), count: counts.red, always: true },
    { state: 'record', label: t('guaranteed.legs.total.record'), count: counts.record, always: false },
    { state: 'shadow', label: t('guaranteed.legs.total.yellow'), count: counts.yellow, always: true },
    { state: 'ok', label: t('guaranteed.legs.total.green'), count: counts.green, always: true },
    { state: 'immune', label: t('guaranteed.legs.total.immune'), count: counts.immune, always: false },
    { state: 'unprobed', label: t('guaranteed.legs.total.unprobed'), count: counts.unprobed, always: false },
    { state: 'manual', label: t('guaranteed.legs.total.grey'), count: counts.grey, always: false },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-label text-paper-mid" data-testid="guaranteed-leg-totals">
      <span
        className={cx('inline-flex items-center gap-1.5', firstRemoved > 0 ? 'text-bad' : 'text-paper-low')}
        title={t('guaranteed.legs.first_removed_tip')}
        data-testid="guaranteed-leg-total-first-removed"
      >
        {t('guaranteed.legs.total.first_removed')}
        <span className={cx('font-mono font-semibold', firstRemoved > 0 ? 'text-bad' : 'text-paper')}>{firstRemoved}</span>
      </span>
      {items.filter((item) => item.always || item.count > 0).map((item) => (
        <span key={item.state} className="inline-flex items-center gap-1.5" data-testid={`guaranteed-leg-total-${item.state}`}>
          <LegLampMark state={item.state} />
          {item.label}
          <span className="font-mono font-semibold text-paper">{item.count}</span>
        </span>
      ))}
    </div>
  );
}

function legTitle(sku: LegSku, index: number, t: ReturnType<typeof useT>): string {
  const placed = sku.legs[index];
  if (placed === undefined) return '';
  const { head } = placed.leg;
  const name = `${head.upstream_name} · ${head.physical_model} · ${head.upstream_channel_id}`;
  const state = legStateLabel(placed.leg.state, null, t);
  const base = placed.section === 'dead' || placed.section === 'overridden'
    ? t('guaranteed.legs.lamp_title_dead', { leg: name, state })
    : t('guaranteed.legs.lamp_title', { leg: name, state });
  const takeover = takeoverText(placed, t);
  return takeover.length === 0 ? base : `${base}\n${takeover}`;
}

type LampGroup = 'first' | 'reserve' | 'manual' | 'dim';

const GROUP_TITLE_KEY = {
  first: 'guaranteed.legs.lamps.first',
  reserve: 'guaranteed.legs.lamps.reserve',
  manual: 'guaranteed.legs.lamps.manual',
  dim: 'guaranteed.legs.lamps.dead',
} as const;

function lampGroup(section: PlacedLeg['section']): LampGroup {
  switch (section) {
    case 'first':
    case 'reserve':
    case 'manual':
      return section;
    case 'overridden':
    case 'dead':
      return 'dim';
  }
}

/**
 * 首发 ┃ 兜底 ┆ 手动摘除 ┆ 不接流量: a firm divider before the fallback
 * legs, faint ones before the legs that take no traffic.
 */
function SkuLamps({ sku }: { readonly sku: LegSku }): JSX.Element {
  const t = useT();
  if (sku.legs.length === 0) return <span className="text-micro text-paper-low">{t('guaranteed.legs.no_legs')}</span>;
  const indexed = sku.legs.map((placed, index) => ({ placed, index }));
  const groups = (['first', 'reserve', 'manual', 'dim'] as const)
    .map((group) => ({ group, entries: indexed.filter((entry) => lampGroup(entry.placed.section) === group) }))
    .filter((entry) => entry.entries.length > 0);
  return (
    <span className="inline-flex max-w-[150px] flex-wrap items-center justify-end gap-1" data-testid={`guaranteed-leg-lamps-${sku.skuId}`}>
      {groups.map(({ group, entries }, position) => (
        <span key={group} className="contents">
          {position === 0 ? null : (
            <span
              aria-hidden="true"
              className={cx('mx-0.5 w-px', group === 'reserve' ? 'h-3 bg-line-strong' : 'h-2 bg-line')}
              data-testid={`guaranteed-leg-lamps-divider-${sku.skuId}`}
              data-before={group}
            />
          )}
          <span
            className="inline-flex items-center gap-1"
            title={t(GROUP_TITLE_KEY[group], { count: String(entries.length) })}
            data-testid={`guaranteed-leg-lamps-${sku.skuId}-${group}`}
          >
            {entries.map((entry) => (
              <LegLampMark
                key={entry.placed.leg.key}
                state={entry.placed.leg.state}
                size={group === 'dim' ? 'sm' : 'md'}
                reachable={group !== 'dim'}
                title={legTitle(sku, entry.index, t)}
              />
            ))}
          </span>
        </span>
      ))}
    </span>
  );
}

interface ModePart {
  readonly name: string;
  readonly mode: GuaranteedMode;
  readonly text: string;
}

function modeParts(sku: LegSku, t: ReturnType<typeof useT>): readonly ModePart[] {
  const fallbackOrgs = overlayFallbackOnCount(sku);
  const { row } = sku;
  if (row === null) {
    return [{
      name: t('guaranteed.legs.rail.fallback'),
      mode: fallbackOrgs > 0 ? 'act' : 'off',
      text: fallbackOrgs > 0
        ? t('guaranteed.legs.rail.fallback_overlay_on', { count: String(fallbackOrgs) })
        : t(fallbackLabelKey('off')),
    }];
  }
  const probe = skuProbeMode(row);
  const chain = skuChainMode(row);
  const jev: GuaranteedMode = isGuaranteedJevMode(row.jev_judge) ? row.jev_judge : 'off';
  const fallback = skuFallbackMode(row);
  return [
    { name: t('guaranteed.legs.rail.probe'), mode: probe, text: t(modeLabelKey(probe)) },
    { name: t('guaranteed.legs.rail.chain'), mode: chain, text: t(modeLabelKey(chain)) },
    { name: t('guaranteed.legs.rail.jev'), mode: jev, text: t(modeLabelKey(jev)) },
    {
      name: t('guaranteed.legs.rail.fallback'),
      mode: fallback,
      text: fallback !== 'act' && fallbackOrgs > 0
        ? t('guaranteed.legs.rail.fallback_orgs', { mode: t(fallbackLabelKey(fallback)), count: String(fallbackOrgs) })
        : t(fallbackLabelKey(fallback)),
    },
  ];
}

/** One muted, truncated line; the full text is the hover title. */
function ModeLine({ sku }: { readonly sku: LegSku }): JSX.Element | null {
  const t = useT();
  if (!sku.configured) return null;
  const parts = modeParts(sku, t);
  const lead = sku.row === null ? [t('guaranteed.legs.rail.overlay_only')] : [];
  const title = [...lead, ...parts.map((part) => `${part.name} ${part.text}`)].join(' · ');
  return (
    <span className="block truncate text-micro text-paper-low" title={title}>
      {lead.map((text) => <span key={text}>{`${text} · `}</span>)}
      {parts.map((part, index) => (
        <span key={part.name}>
          {index > 0 ? ' · ' : ''}
          {part.name}
          {' '}
          <span className={MODE_TEXT_TONE[part.mode]}>{part.text}</span>
        </span>
      ))}
    </span>
  );
}

function SkuItem({ sku, selected, onSelect }: {
  readonly sku: LegSku;
  readonly selected: boolean;
  readonly onSelect: () => void;
}): JSX.Element {
  const t = useT();
  const slash = sku.skuName.indexOf('/');
  const short = slash >= 0 ? sku.skuName.slice(slash + 1) : sku.skuName;
  const danger = sku.danger;
  const live = countLegStates(liveStates([sku]));
  const ariaLabel = [
    sku.skuName,
    ...(sku.firstRemoved > 0 ? [t('guaranteed.legs.first_removed_badge', { count: String(sku.firstRemoved) })] : []),
    ...(sku.takeovers > 0 ? [t('guaranteed.legs.takeover_badge')] : []),
    t('guaranteed.legs.ns_red', { count: String(live.red) }),
    t('guaranteed.legs.ns_yellow', { count: String(live.yellow) }),
  ].join(' · ');
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected}
      aria-label={ariaLabel}
      title={ariaLabel}
      className={cx(
        'group flex w-full min-w-0 flex-col gap-1 border-l-2 py-2 pl-3 pr-2.5 text-left transition-colors duration-120 hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-signal/40',
        danger && 'bg-bad-dim/30',
        selected ? 'border-signal bg-raised' : danger ? 'border-bad' : 'border-transparent',
      )}
      data-testid={`guaranteed-leg-sku-${sku.skuId}`}
      data-danger={danger ? (sku.firstRemoved > 0 ? 'first-removed' : 'takeover') : undefined}
    >
      <span className="flex items-center gap-2">
        <span className={cx(
          'min-w-0 flex-1 truncate font-mono text-dense',
          selected ? 'text-paper' : 'text-paper-mid group-hover:text-paper',
        )}
        >
          {short}
        </span>
        <SkuLamps sku={sku} />
      </span>
      <ModeLine sku={sku} />
    </button>
  );
}

function NamespaceBoxes({ skus, prefix, closed, selected, onToggle, onSelect }: {
  readonly skus: readonly LegSku[];
  readonly prefix: string;
  readonly closed: ReadonlySet<string>;
  readonly selected: string | null;
  readonly onToggle: (token: string) => void;
  readonly onSelect: (skuId: string) => void;
}): JSX.Element {
  const t = useT();
  return (
    <>
      {groupSkusByNamespace(skus).map((group) => {
        const namespaceKey = `${prefix}${group.namespace === '' ? 'other' : group.namespace}`;
        const open = !closed.has(`ns:${namespaceKey}`);
        const counts = countLegStates(liveStates(group.skus));
        return (
          <div key={namespaceKey} className="rounded-lg border border-line-soft bg-well" data-testid={`guaranteed-leg-namespace-${namespaceKey}`}>
            <button
              type="button"
              aria-expanded={open}
              onClick={() => onToggle(`ns:${namespaceKey}`)}
              className="flex w-full items-center gap-1.5 rounded-lg px-2.5 py-2 text-left hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-signal/40"
            >
              <span className="text-paper-low">
                {open
                  ? <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
                  : <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />}
              </span>
              <span className="min-w-0 flex-1 truncate font-mono text-dense text-paper">
                {group.namespace === '' ? t('guaranteed.leg.namespace_other') : group.namespace}
              </span>
              <span className="inline-flex items-center gap-1.5 text-micro text-paper-low">
                {firstRemovedCount(group.skus) > 0 ? (
                  <span className="font-semibold text-bad" data-testid={`guaranteed-leg-ns-first-removed-${namespaceKey}`}>
                    {t('guaranteed.legs.ns_first_removed', { count: String(firstRemovedCount(group.skus)) })}
                  </span>
                ) : null}
                {counts.red > 0 ? <span className="text-bad">{t('guaranteed.legs.ns_red', { count: String(counts.red) })}</span> : null}
                {counts.yellow > 0 ? <span className="text-warn">{t('guaranteed.legs.ns_yellow', { count: String(counts.yellow) })}</span> : null}
                <span>{t('guaranteed.legs.ns_skus', { count: String(group.skus.length) })}</span>
              </span>
            </button>
            {open ? (
              <div className="border-t border-line-soft py-1">
                {group.skus.map((sku) => (
                  <SkuItem key={sku.skuId} sku={sku} selected={sku.skuId === selected} onSelect={() => onSelect(sku.skuId)} />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

export interface LegRailProps {
  readonly configured: readonly LegSku[];
  readonly shownConfigured: readonly LegSku[];
  readonly others: readonly LegSku[];
  readonly shownOthers: readonly LegSku[];
  readonly query: string;
  readonly onQuery: (value: string) => void;
  readonly abnormalOnly: boolean;
  readonly onAbnormalOnly: (value: boolean) => void;
  readonly closed: ReadonlySet<string>;
  readonly onToggle: (token: string) => void;
  readonly otherOpen: boolean;
  readonly onOtherOpen: (open: boolean) => void;
  readonly selected: string | null;
  readonly onSelect: (skuId: string) => void;
}

export function LegRail(props: LegRailProps): JSX.Element {
  const t = useT();
  const { configured, shownConfigured, others, shownOthers } = props;
  return (
    <aside
      className="flex max-h-[calc(100vh-220px)] w-80 flex-none flex-col gap-2 overflow-y-auto pr-1"
      aria-label={t('guaranteed.section.legs')}
      data-testid="guaranteed-leg-rail"
    >
      <div className="flex flex-col gap-2 px-0.5 py-1">
        <Totals
          counts={{ ...countLegStates(liveStates(configured)), grey: manualLegCount(configured) }}
          firstRemoved={firstRemovedCount(configured)}
        />
        <Input
          value={props.query}
          onChange={(event) => props.onQuery(event.currentTarget.value)}
          placeholder={t('guaranteed.leg.search_placeholder')}
          aria-label={t('guaranteed.leg.search')}
          data-testid="guaranteed-leg-search"
        />
        <Checkbox
          checked={props.abnormalOnly}
          onCheckedChange={props.onAbnormalOnly}
          label={t('guaranteed.legs.abnormal')}
          testId="guaranteed-leg-abnormal"
        />
      </div>
      <div className="px-0.5 pt-2 text-label text-paper-low">
        {shownConfigured.length === configured.length
          ? t('guaranteed.legs.section_configured', { count: String(configured.length) })
          : t('guaranteed.legs.section_configured_filtered', {
            shown: String(shownConfigured.length),
            count: String(configured.length),
          })}
      </div>
      {shownConfigured.length === 0 ? (
        <p className="m-0 px-3 py-3 text-label text-paper-low">{t('guaranteed.legs.none_match')}</p>
      ) : (
        <NamespaceBoxes
          skus={shownConfigured}
          prefix=""
          closed={props.closed}
          selected={props.selected}
          onToggle={props.onToggle}
          onSelect={props.onSelect}
        />
      )}
      {others.length === 0 ? null : (
        <>
          <div className="px-0.5 pt-2 text-label text-paper-low">
            <button
              type="button"
              aria-expanded={props.otherOpen}
              onClick={() => props.onOtherOpen(!props.otherOpen)}
              className="inline-flex items-center gap-1 rounded-sm hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
              data-testid="guaranteed-leg-other-toggle"
            >
              {props.otherOpen
                ? <ChevronDown aria-hidden="true" className="h-3 w-3" />
                : <ChevronRight aria-hidden="true" className="h-3 w-3" />}
              {t('guaranteed.legs.section_other', {
                count: shownOthers.length === others.length
                  ? String(others.length)
                  : `${String(shownOthers.length)} / ${String(others.length)}`,
              })}
            </button>
          </div>
          {props.otherOpen ? (
            shownOthers.length === 0 ? (
              <p className="m-0 px-3 py-3 text-label text-paper-low">{t('guaranteed.legs.other_none')}</p>
            ) : (
              <NamespaceBoxes
                skus={shownOthers}
                prefix="other:"
                closed={props.closed}
                selected={props.selected}
                onToggle={props.onToggle}
                onSelect={props.onSelect}
              />
            )
          ) : null}
        </>
      )}
    </aside>
  );
}
